import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAction, useApi } from '../lib/useApi';
import { honestLine, languageLabel, useLanguageCatalogue, type Language } from '../lib/languages';
import { ErrorBox, Loading, Notice, PageHeader } from '../components/ui';

interface Dialect {
  code: string;
  language_code: string;
  name: string;
  english_name: string;
  region: string | null;
}

/**
 * Living languages.
 *
 * Two honest facts on one page: every language here works for your family's
 * stories today, and exactly one of them has lessons written. A family looking
 * for Igbo lessons should leave knowing that, not discover it three screens in.
 */
export function LanguagesPage() {
  const { catalogue, loading, error } = useLanguageCatalogue();
  const dialects = useApi<{ dialects: Dialect[] }>('/dialects');
  const act = useAction();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  // Which languages this family has asked for, and any problem asking. Kept per
  // language so the answer appears beside the button that was pressed — a
  // confirmation at the top of a long page is a confirmation nobody sees.
  const [asked, setAsked] = useState<Record<string, string>>({});
  const [askError, setAskError] = useState<Record<string, string>>({});
  const [asking, setAsking] = useState<string | null>(null);

  const byLanguage = useMemo(() => {
    const m = new Map<string, Dialect[]>();
    for (const d of dialects.data?.dialects ?? []) {
      const list = m.get(d.language_code) ?? [];
      list.push(d);
      m.set(d.language_code, list);
    }
    return m;
  }, [dialects.data]);

  if (loading) return <Loading label="Loading the languages…" />;
  if (error || !catalogue) return <ErrorBox error={error} />;

  const q = query.trim().toLowerCase();
  const regions = catalogue.regions
    .map((r) => ({ ...r, languages: r.languages.filter((l) => !q || l.name.toLowerCase().includes(q) || l.native_name.toLowerCase().includes(q)) }))
    .filter((r) => r.languages.length > 0);
  const found = regions.reduce((n, r) => n + r.languages.length, 0);

  return (
    <>
      <PageHeader
        kicker="Explore"
        title="Living languages"
        termKey="page.languages"
        lead={`${catalogue.total} languages, grouped the way the continent actually sits. Every one of them works for your family's stories, voices and Grandparent Calls today. Lessons and games are written for Yorùbá first — we would rather do one language properly than claim ninety.`}
      />
      <Notice text={act.notice} />
      <ErrorBox error={act.error} />

      <div className="card row" style={{ marginBottom: 16 }}>
        <input
          aria-label="Find a language"
          placeholder="Find your family's language…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <span className="muted small">{q ? `${found} match${found === 1 ? '' : 'es'}` : `${catalogue.total} languages`}</span>
      </div>

      {found === 0 && (
        <section className="card stack">
          <h2>Help us bring this language to Ìran</h2>
          <p className="muted">
            We have not listed it yet. Tell us its name and we will add it to the catalogue — a language reaches the vault long before it reaches the lessons.
          </p>
        </section>
      )}

      {regions.map((r) => (
        <section key={r.region} className="card stack">
          <h2>{r.region}</h2>
          <div className="lang-grid">
            {r.languages.map((l: Language) => {
              const varieties = byLanguage.get(l.code) ?? [];
              const expanded = open === l.code;
              return (
                <div key={l.code} className={`lang-tile ${l.status === 'lessons_live' ? 'live' : ''}`}>
                  <button className="lang-tile-head" onClick={() => setOpen(expanded ? null : l.code)} aria-expanded={expanded}>
                    <span className="lang-name yo" lang={l.code}>
                      {languageLabel(l)}
                    </span>
                    {/* Say what a family gets, not an internal status name. */}
                    <span className={`badge ${l.status === 'lessons_live' ? 'ok' : ''}`}>
                      {l.status === 'lessons_live' ? 'lessons + stories' : l.status === 'stories_only' ? 'stories' : 'planned'}
                    </span>
                  </button>
                  {expanded && (
                    <div className="stack tight lang-tile-body">
                      <p className="small">{honestLine(l)}</p>
                      {l.speakers_note && <p className="muted small">{l.speakers_note}</p>}
                      {varieties.length > 0 && (
                        <p className="small">
                          <strong>Varieties we know:</strong>{' '}
                          {varieties.map((d) => (
                            <span key={d.code} className="chip yo">
                              {d.name}
                            </span>
                          ))}
                        </p>
                      )}
                      {l.status !== 'lessons_live' &&
                        (asked[l.code] ? (
                          <p className="asked" role="status">
                            <span aria-hidden="true">✓</span> {asked[l.code]}
                          </p>
                        ) : (
                          <>
                            <Volunteer code={l.code} name={l.name} />
                            <button
                              className="btn small secondary"
                              disabled={asking === l.code}
                              onClick={async () => {
                                setAsking(l.code);
                                setAskError({ ...askError, [l.code]: '' });
                                try {
                                  const r = await api<{ alreadyRequested?: boolean; message?: string }>(`/languages/${l.code}/request`, { body: {} });
                                  setAsked({
                                    ...asked,
                                    [l.code]: r.message ?? (r.alreadyRequested ? `Already noted — you asked for ${l.name} before.` : `Noted. We will tell you when ${l.name} lessons begin.`),
                                  });
                                } catch (e) {
                                  setAskError({ ...askError, [l.code]: e instanceof ApiError ? e.message : 'Could not send that just now — please try again.' });
                                } finally {
                                  setAsking(null);
                                }
                              }}
                            >
                              {asking === l.code ? 'Sending…' : `Ask for ${l.name} lessons`}
                            </button>
                            {askError[l.code] && (
                              <p className="small" role="alert" style={{ color: 'var(--danger)' }}>
                                {askError[l.code]}
                              </p>
                            )}
                          </>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}


/**
 * "I speak this — I will help build it."
 *
 * Ninety-three languages have a thirty-lesson plan and nobody to write it.
 * This is where that stops being hypothetical. A volunteer is a name on a list
 * a safeguarding admin reads: nobody's words reach a child because they filled
 * in a form.
 */
function Volunteer({ code, name }: { code: string; name: string }) {
  const act = useAction();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ displayName: '', email: '', about: '', canRecord: false });

  if (done) {
    return (
      <p className="asked" role="status">
        <span aria-hidden="true">✓</span> Thank you — we will write to you before any of your words reach a child.
      </p>
    );
  }
  if (!open) {
    return (
      <button className="btn small ghost" onClick={() => setOpen(true)}>
        I speak {name} — I can help
      </button>
    );
  }
  return (
    <form
      className="stack tight volunteer"
      onSubmit={async (e) => {
        e.preventDefault();
        const ok = await act.run(() =>
          api(`/languages/${code}/volunteer`, {
            body: { displayName: form.displayName, email: form.email, about: form.about || undefined, canWrite: true, canRecord: form.canRecord },
          }),
        );
        if (ok) setDone(true);
      }}
    >
      <span className="label">Help build {name}</span>
      <input placeholder="Your name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required maxLength={80} aria-label="Your name" />
      <input type="email" placeholder="Email we can reach you on" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required aria-label="Your email" />
      <textarea
        rows={2}
        placeholder="Where you are from, and how your side speaks it"
        value={form.about}
        onChange={(e) => setForm({ ...form, about: e.target.value })}
        aria-label="About how you speak it"
      />
      <label className="check">
        <input type="checkbox" checked={form.canRecord} onChange={(e) => setForm({ ...form, canRecord: e.target.checked })} /> I could record my voice too
      </label>
      <ErrorBox error={act.error} />
      <div className="row">
        <button className="btn small" disabled={act.busy}>
          {act.busy ? 'Sending…' : 'Offer to help'}
        </button>
        <button type="button" className="btn small ghost" onClick={() => setOpen(false)}>
          Not now
        </button>
      </div>
    </form>
  );
}
